import { Component, ContentType, Field } from 'contensis-core-api';
import { Entry } from 'contensis-management-api';
import { FormContentType } from '~/models/FormContentTypeManagement';
import { IContensisContext } from '~/models/IContensisContext';
import ContensisRepository from '~/repositories/ContensisRepository';
import { getSysId } from '~/util/EntryHelper';
import { isArray } from '~/util/typeguards';

export type ContentTypeFields = {
  [k: string]: ContentTypeField;
};

/**
 * Supplying the field configuration from the target content type, the context containing
 * the content models for the parent entry *and the target repository, we can take a given value
 * from the source and build a complete picture of the field specifically for the target entry
 * checking dependencies and setting flag to trigger a two-pass loading status on the entry if
 * any dependents do not exist
 * Using the toJSON method to produce the entry as JSON for loading into the Management API
 */
export class ContentTypeField {
  requireSecondPass = false;
  /**
   * [defaultId, originalId]
   */
  defaults: [string, string][] = [];
  /**
   * [rejectedId, existingId]
   * when we have opted to use an existing default and we might not
   * need to migrate the default entry after all
   */
  rejectedDefaults: [string, string][] = [];

  private _firstPassValue?: Field; // containing no dependency data
  private _finalValue!: Field; // containing all dependency data transformed for the target
  // A composer or component field in an entry will have their own built fields
  fields: ContentTypeFields | ContentTypeField[] | undefined = undefined;

  set firstPassValue(value: Field | undefined) {
    const { resolve, transform } = this.context;
    const field = transform.RenameKeysAndValues(
      value,
      resolve.language(this.resource),
      this.contensis.project.primaryLanguage
    );
    this._firstPassValue = field;
  }

  get firstPassValue() {
    return this._firstPassValue;
  }

  set finalValue(value: Field) {
    if (!value) {
      this._finalValue = value;
      return;
    }
    const { payload, resolve, transform } = this.context;
    let field = transform.RenameKeysAndValues(
      value,
      resolve.language(this.resource),
      this.contensis.project.primaryLanguage
    );

    if (payload.readOnlyFields)
      field = transform.SetFieldEditorProperty(field, 'readOnly', true);

    this._finalValue = field;
  }

  get finalValue() {
    return this._finalValue;
  }

  constructor(
    public field: Field, // from the source content type
    public parent: Field | undefined, // from the source content type
    public existing: Field | undefined, // from the target content type
    private resource: ContentType | Component | FormContentType,
    private context: IContensisContext,
    private contensis: ContensisRepository
  ) {
    this.field = field;
    this.existing = existing;
    this.parent = parent;
    // Preemptively set finalValue
    this.finalValue = field;
    // Build the field, check dependencies and set the values for first pass and final
    this.check();
  }

  /**
   * Return a final value for the field, iterating over any built fields if applicable
   * @returns entry as JSON
   */
  toJSON = (opts = { firstPass: false }) => {
    const { contensis, field, resource } = this;
    const { context } = contensis;
    const { assert } = context.field;
    const fieldJson =
      opts.firstPass === true &&
      (assert.IsEntry(field) || assert.IsCanvas(field)) &&
      this.requireSecondPass
        ? (this.firstPassValue as Field)
        : this.finalValue;

    // Iterate over builtFields and return finalValue
    if (assert.IsComposer(field)) {
      const composerFields: Field[] = [];

      for (const innerField of this.fields as ContentTypeField[]) {
        const composerItem = innerField.toJSON(opts);
        // Since omitting taxonomy fields we might be left with fields with value as `undefined`
        if (composerItem) composerFields.push(composerItem);
      }

      // Build up potentially undefined validations.allowedFields
      if (!fieldJson.validations) fieldJson.validations = {};
      if (!fieldJson.validations.allowedFieldTypes)
        fieldJson.validations.allowedFieldTypes = { fields: composerFields };
      else fieldJson.validations.allowedFieldTypes.fields = composerFields;
    } else if (
      assert.IsComponent(field) &&
      context.assert.isComponent(resource)
    ) {
      const componentFields: Field[] = [];
      for (const innerField of Object.values(this.fields as ContentTypeFields))
        componentFields.push(innerField.toJSON(opts));
      // Cannot assign to fields as it is readonly
      if (componentFields.length) (fieldJson as any).fields = componentFields;
    }

    // Remove validations if requested, we cannot do this earlier as we need to
    // build all other content types first to know what ids to set in allowedContentTypes[]
    // and allowedTypes.ids[]
    if (context.payload.includeValidations === false) {
      context.transform.RemoveFieldValidations(fieldJson, contensis);
    }

    return fieldJson;
  };

  /**
   * Check the content type field for any special types and process various values
   * setting the first pass and final values
   */
  check = () => {
    const { assert } = this.context.field;
    const { field } = this;

    // Entry-type field
    if (assert.IsEntry(field) || assert.IsAsset(field) || assert.IsImage(field))
      this.finalValue = this.#buildEntryLink();
    // Component field
    else if (assert.IsComponent(field))
      this.finalValue = this.#buildComponent();
    // Composer field
    else if (assert.IsComposer(field)) this.finalValue = this.#buildComposer();
    // Taxonomy field - deprecated so don't migrate unless it already exists in the target
    else if (assert.IsTaxonomy(field)) {
      this.finalValue = this.existing as any;
    }
  };

  #buildEntryLink = (): Field => {
    const { contensis, context, field, existing } = this;
    if (!context.payload.includeDefaults) {
      field.default = existing?.default;
      this.#validateAllowedContentTypes();
      return field;
    }

    if (!field.default) {
      // Hack to make test pass
      if (context.field.assert.IsEntry(field)) delete field.default;
      return field;
    }

    const newDefaults: typeof this.field.default = {};

    const languages = Object.keys(field.default || {});
    for (const language of languages) {
      // Is it a valid language for the target?
      if (!contensis.project.supportedLanguages.find((l) => l === language))
        continue;

      if (existing?.default?.[language]) {
        // The language variation already has an existing default entry
        // so we can preserve this default link
        newDefaults[language] =
          field.dataType === existing.dataType
            ? existing.default[language] // field and existing dataTypes match
            : field.dataType === 'objectArray'
              ? [existing.default[language]] // field is array and existing is not
              : existing.default[language][0]; // existing is array and field is not

        // Maintain lists of default values and rejected default values
        // so we know later that defaults were rejected and used nowhere else
        if (isArray(field.default[language])) {
          if (isArray(existing.default[language])) {
            field.default[language].forEach((d, i) => {
              this.rejectedDefaults.push([
                getSysId(d),
                getSysId(existing.default?.[language][i]),
              ]);
              this.defaults.push([
                getSysId(existing.default?.[language][i]),
                getSysId(d),
              ]);
            });
          } else {
            for (const entry of field.default[language] as Entry[]) {
              this.rejectedDefaults.push([
                getSysId(entry),
                getSysId(existing.default[language]),
              ]);
              this.defaults.push([
                getSysId(existing.default[language]),
                getSysId(entry),
              ]);
            }
          }
        } else {
          if (isArray(existing.default[language])) {
            existing.default[language].forEach((ed) => {
              this.rejectedDefaults.push([
                field.default?.[language].sys.id,
                ed.sys.id,
              ]);
              this.defaults.push([ed.sys.id, field.default?.[language].sys.id]);
            });
          } else {
            this.rejectedDefaults.push([
              getSysId(field.default[language]),
              getSysId(existing.default[language]),
            ]);
            this.defaults.push([
              getSysId(newDefaults[language]),
              getSysId(field.default[language]),
            ]);
          }
        }
      } else {
        if (isArray(field.default[language])) {
          if (field.default[language].length) newDefaults[language] = [];
          for (const entry of field.default[language]) {
            if (!contensis.entries.findDependent(entry)) {
              // Can't find the dependent entry
              this.requireSecondPass = true;
              this.firstPassValue = this.#blankDefaultEntry(field);
            }
            if (field.dataFormat === 'image') {
              const transformed = this.#transformDefaultImageSys(entry);
              newDefaults[language].push(transformed);
              this.defaults.push([
                transformed.asset.sys.id as string,
                entry.asset.sys.id,
              ]);
            } else {
              const transformed = this.#transformDefaultEntrySys(entry);
              newDefaults[language].push(transformed);
              this.defaults.push([transformed.sys.id as string, entry.sys.id]);
            }
          }
        } else {
          const defaultEntry = field.default[language];
          const entry =
            'asset' in defaultEntry && defaultEntry.asset.sys.id
              ? defaultEntry.asset
              : defaultEntry;
          if (!contensis.entries.findDependent(entry)) {
            // Can't find the dependent entry
            this.requireSecondPass = true;
            this.firstPassValue = this.#blankDefaultEntry(field);
          }
          if (field.dataFormat === 'image')
            newDefaults[language] =
              this.#transformDefaultImageSys(defaultEntry);
          else
            newDefaults[language] = this.#transformDefaultEntrySys(
              field.default[language]
            );
          this.defaults.push([
            getSysId(newDefaults[language]),
            getSysId(field.default[language]),
          ]);
        }
      }
    }

    const defaultValue =
      Object.keys(newDefaults).length === 0 ? undefined : newDefaults;

    // Return the final field with the new default entry link(s)
    this.#validateAllowedContentTypes();
    return {
      ...field,
      default: defaultValue,
    };
  };

  #validateAllowedContentTypes = () => {
    // Ensure allowed content types all exist as there's a chance one or more
    // of the allowed content types have been removed from the source environment
    const allowedContentTypeIds = this.context.field.GetAllowedContentTypes(
      this.field
    );

    // Ensure allowed content types all exist

    const allContentTypeIds = new Set<string>([
      ...this.context.resolve
        .contentTypes(allowedContentTypeIds)
        .map((ct) => ct.id),
      ...this.context.source.context.resolve
        .contentTypes(allowedContentTypeIds)
        .map((ct) => ct.id),
    ]);

    const allowContentTypeIds = [];
    for (const allowedContentTypeId of allContentTypeIds) {
      if (allContentTypeIds.has(allowedContentTypeId)) {
        allowContentTypeIds.push(allowedContentTypeId);
      }
    }
    if ((this.field.validations as any)?.allowedIds?.ids) {
      (this.field.validations as any).allowedIds.ids = allowContentTypeIds;
    }
  };

  // private findDependentEntry = (value: Entry) => {
  //   const findId = this.contensis.transformGuid(value?.sys?.id || '');
  //   return this.contensis.entries.raw.find((e) => e.sys.id === findId);
  // };

  #blankDefaultEntry = (f: Field) => ({ ...f, default: undefined });

  #transformDefaultEntrySys = (entry: Entry) => {
    if (!entry?.sys?.id) return entry;

    const defaultEntryLink = {
      sys: {
        ...(entry?.sys || {}),
        id: this.contensis.transformGuid(entry.sys.id),
      },
    };

    return defaultEntryLink;
  };

  #transformDefaultImageSys = (image: any) => {
    if (!image) return undefined;
    if (Array.isArray(image)) {
      return image.map((v) => ({
        ...v,
        asset: { ...this.#transformDefaultEntrySys(v.asset) },
      }));
    } else {
      return {
        ...image,
        asset: { ...this.#transformDefaultEntrySys(image?.asset) },
      };
    }
  };

  #buildComponent = () => {
    const { contensis, context, field } = this;
    const component = context.resolve.component(
      field.dataFormat,
      context.source.context.components
    );
    const existing = context.resolve.component(field.id);

    this.fields = {}; // Change builtFields to an empty object
    if (component?.fields?.length)
      for (const field of component.fields) {
        const existingField = existing?.fields?.find((f) => f.id === field.id);
        const ctf = new ContentTypeField(
          field,
          this.field,
          existingField,
          this.resource,
          context,
          contensis
        );
        this.fields[field.id] = ctf;
        if (ctf.requireSecondPass) this.requireSecondPass = true;
      }
    return this.toJSON();
  };

  #buildComposer = () => {
    const { contensis, context, existing } = this;
    const composerFields = context.field.GetComposerFields(this.field);
    const existingFields = existing
      ? context.field.GetComposerFields(existing)
      : [];

    this.fields = composerFields.length ? [] : undefined; // Change builtFields to an array
    for (const field of composerFields) {
      const existingField = existingFields.find((f) => f.id === field.id);
      const ctf = new ContentTypeField(
        field,
        this.field,
        existingField,
        this.resource,
        context,
        contensis
      );
      this.fields?.push(ctf);
      if (ctf.requireSecondPass) this.requireSecondPass = true;
    }
    return this.toJSON();
  };
}
